/**
 * GDI Audience Targeting
 *
 * Every GDI block in the Optimizely schema carries a
 * `${__typename}AudienceTargeting` field shaped as `{Type, VariantId}`.
 *
 * Supported targeting today:
 *   - Type        : "Authentication Status"
 *   - VariantId   : "loggedin" | "guest"
 *
 * Render rules:
 *   - VariantId === "loggedin"  → render only when the customer is logged in.
 *   - VariantId === "guest"     → render only for guest (anonymous) visitors.
 *   - Any other Type / empty value / unknown variant → render for everyone
 *     (back-compat with content authored before this field existed).
 *
 * This module exposes a pure matcher (`matchesAudienceTargeting`) so unit
 * tests / SSR contexts can resolve targeting without touching React, and a
 * hook (`useIsAudienceTargetMatch`) for use inside components.
 */
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'

export const AUDIENCE_TARGETING_TYPE_AUTHENTICATION_STATUS = 'Authentication Status'
export const AUDIENCE_VARIANT_LOGGED_IN = 'loggedin'
export const AUDIENCE_VARIANT_GUEST = 'guest'

/**
 * Pure check — returns `true` when the block should render for a customer in
 * the supplied state. Unknown / empty targeting always renders so missing
 * configuration never accidentally hides content.
 *
 * @param {{Type?: string, VariantId?: string} | null | undefined} audienceTargeting
 * @param {{isLoggedIn?: boolean}} [state]
 * @returns {boolean}
 */
export const matchesAudienceTargeting = (audienceTargeting, {isLoggedIn} = {}) => {
    if (!audienceTargeting || typeof audienceTargeting !== 'object') return true

    const {Type, VariantId} = audienceTargeting
    if (Type !== AUDIENCE_TARGETING_TYPE_AUTHENTICATION_STATUS) return true

    const variant = String(VariantId ?? '').trim().toLowerCase()
    if (variant === AUDIENCE_VARIANT_LOGGED_IN) return Boolean(isLoggedIn)
    if (variant === AUDIENCE_VARIANT_GUEST) return !isLoggedIn

    return true
}

/**
 * React hook — resolves the current customer's logged-in state and returns
 * `true` when the block should render. Components should call this once at
 * the top of their render (before any conditional early returns) so hook
 * order is preserved across re-renders.
 *
 * @param {{Type?: string, VariantId?: string} | null | undefined} audienceTargeting
 * @returns {boolean}
 */
export const useIsAudienceTargetMatch = (audienceTargeting) => {
    const {data: customer} = useCurrentCustomer()
    const isLoggedIn = customer?.isRegistered ?? false
    return matchesAudienceTargeting(audienceTargeting, {isLoggedIn})
}
